import type {
  Modification,
  ModificationGroupLimits,
  ModificationsMap,
  PairedModifierGroupMap,
} from '../../types';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Checks if any active extras modifications can be moved to now-available included slots
 * in the same paired group.
 *
 * For each included group with available capacity, finds extras with a paired
 * included counterpart and swaps them — removing the upcharge. Extras without
 * a paired included modification or without available slots are kept as-is.
 */
export function downgradeExtrasToIncluded(params: Params): Modification[] {
  const {
    activeModifications,
    includedModificationsMap,
    includedModifierGroupsByExtrasName,
    modificationLimits,
  } = params;

  const capacity = getIncludedGroupCapacity({
    activeModifications,
    includedModifierGroupsByExtrasName,
    modificationLimits,
  });

  if (capacity.size === 0) {
    return activeModifications;
  }

  const downgradeModification = downgradeModificationFactory({
    includedModificationsMap,
    capacity,
  });

  return activeModifications.reduce<Modification[]>(downgradeModification, []);
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function downgradeModificationFactory(params: DowngradeFactoryParams) {
  const { includedModificationsMap, capacity } = params;

  return function (items: Modification[], modification: Modification): Modification[] {
    //
    // 1. Skip non-extras — only extras can be downgraded.

    if (!modification.isExtra || !modification.ingredientId) {
      return [...items, modification];
    }

    // 2. Skip extras without a paired included counterpart.

    const includedCounterpart = includedModificationsMap.get(modification.ingredientId);

    if (!includedCounterpart) {
      return [...items, modification];
    }

    // 3. Skip if the included group has no available slots.

    const { modificationGroupName } = includedCounterpart;
    const available = capacity.get(modificationGroupName) ?? 0;

    if (available <= 0) {
      return [...items, modification];
    }

    // 4. Move as many portions as possible to the included group.

    const quantityToDowngrade = Math.min(modification.quantity, available);
    const remainingExtras = modification.quantity - quantityToDowngrade;

    capacity.set(modificationGroupName, available - quantityToDowngrade);

    const downgraded = withQuantity(includedCounterpart, quantityToDowngrade);

    // 5. Keep the remainder as paid extras if not all portions fit.

    if (remainingExtras > 0) {
      return [...items, downgraded, withQuantity(modification, remainingExtras)];
    }

    return [...items, downgraded];
  };
}

// ─────────────────────────────────────────────────────────────────────────────

function getIncludedGroupCapacity(params: CapacityParams) {
  const { activeModifications, includedModifierGroupsByExtrasName, modificationLimits } = params;

  const activeCounts = activeModifications.reduce<GroupCountsByName>(accumulateGroupCounts, {});
  const computeCapacity = computeCapacityFactory({ activeCounts, modificationLimits });

  return Object.values(includedModifierGroupsByExtrasName).reduce(computeCapacity, new Map());
}

function accumulateGroupCounts(counts: GroupCountsByName, modification: Modification) {
  const { modificationGroupName, quantity } = modification;

  const currentQuantity = counts[modificationGroupName] ?? 0;
  const updatedQuantity = currentQuantity + quantity;

  return {
    ...counts,
    [modificationGroupName]: updatedQuantity,
  };
}

function computeCapacityFactory(params: ComputeCapacityFactoryParams) {
  const { activeCounts, modificationLimits } = params;

  return function (capacity: GroupCapacityByName, includedGroupName: string) {
    if (capacity.has(includedGroupName)) return capacity;

    const max = modificationLimits[includedGroupName]?.maxAggregateQuantity;

    if (max === undefined) return capacity;

    const available = max - (activeCounts[includedGroupName] ?? 0);

    if (available <= 0) return capacity;

    capacity.set(includedGroupName, available);

    return capacity;
  };
}

function withQuantity(modification: Modification, quantity: number): Modification {
  return { ...modification, quantity };
}

// ─── Types ──────────────────────────────────────────────────────────────────

type Params = {
  activeModifications: Modification[];
  includedModificationsMap: ModificationsMap;
  includedModifierGroupsByExtrasName: PairedModifierGroupMap;
  modificationLimits: ModificationGroupLimits;
};

type CapacityParams = {
  activeModifications: Modification[];
  includedModifierGroupsByExtrasName: PairedModifierGroupMap;
  modificationLimits: ModificationGroupLimits;
};

type GroupCountsByName = Record<string, number>;

type GroupCapacityByName = Map<string, number>;

type DowngradeFactoryParams = {
  includedModificationsMap: ModificationsMap;
  capacity: GroupCapacityByName;
};

type ComputeCapacityFactoryParams = {
  activeCounts: GroupCountsByName;
  modificationLimits: ModificationGroupLimits;
};
